import {
  AdminLevel,
  CharacterBaseStats,
  type CharacterMapInfo,
  CharacterSecondaryStats,
  type Ecf,
  type EcfRecord,
  type Eif,
  type EifRecord,
  type Emf,
  type Enf,
  type EnfRecord,
  EquipmentPaperdoll,
  type Esf,
  type EsfRecord,
  type FileType,
  InitInitClientPacket,
  type ItemMapInfo,
  MapType,
  NearbyInfo,
  type NpcKilledData,
  type NpcMapInfo,
  type NpcType,
  type OnlinePlayer,
  RefreshRequestClientPacket,
  type ServerSettings,
  type Spell,
  Version,
  Weight,
} from 'eolib';
import { Application, Container } from 'pixi.js';
import { Atlas } from '@/atlas';
import { PacketBus } from '@/bus';
import { clearRectangles } from '@/collision';
import { getDefaultConfig, loadConfig } from '@/config';
import { HALF_TILE_HEIGHT, INITIAL_IDLE_TICKS, MAX_CHALLENGE } from '@/consts';
import {
  AlertController,
  AnimationController,
  AudioController,
  AuthenticationController,
  BankController,
  BarberController,
  BardController,
  BoardController,
  ChatController,
  ChestController,
  CleanupController,
  CommandController,
  ConfigController,
  DrunkController,
  ForgeController,
  GuildController,
  HotbarController,
  InnController,
  InventoryController,
  ItemProtectionController,
  JukeboxController,
  KeyboardController,
  LockerController,
  MapController,
  MarriageController,
  MouseController,
  MovementController,
  NpcController,
  PartyController,
  PingController,
  QuakeController,
  QuestController,
  type SavedSession,
  SessionController,
  ShopController,
  SocialController,
  SpellController,
  StatSkillController,
  StatsController,
  ToastController,
  TradeController,
  UsageController,
  ViewportController,
} from '@/controllers';
import { getEcf, getEdf, getEif, getEmf, getEnf, getEsf } from '@/db';
import { DialogResourceID, type Edf, EOResourceID } from '@/edf';
import { Sans11Font } from '@/fonts';
import { GameState } from '@/game-state';
import { WarpFrame } from '@/warp-frame';
import { WarpPreloader } from '@/warp-preloader';
import { getDeviceId } from './device-id';
import { GfxLoader } from '@/gfx';
import { registerAllHandlers } from '@/handlers';
import { defaultLocale, formatLocaleString, locales } from '@/locale';
import { MapRenderer } from '@/map';
import { MinimapRenderer } from '@/minimap';
import { PacketLogStore } from '@/packet-log';
import { CharacterDeathAnimation, NpcDeathAnimation } from '@/render';
import { SfxId, setAudioController } from '@/sfx';
import type { ISlot } from '@/ui/enums';
import type {
  EffectMetadata,
  NPCMetadata,
  ShieldMetadata,
  WeaponMetadata,
} from '@/utils';
import {
  capitalize,
  EffectAnimationType,
  getEffectMetaData,
  getHatMetadata,
  getNpcMetaData,
  getShieldMetaData,
  getWeaponMetaData,
  HatMaskType,
  isoToScreen,
  randomRange,
  screenToIso,
} from '@/utils';
import type { Vector2 } from '@/vector';

function humanizeEnumName(name: string | undefined): string {
  if (!name) {
    return '';
  }

  return name
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export class Client {
  private stateChangedSubscribers: ((state: GameState) => void)[] = [];
  loginPending = false;
  loginStatus = "";
  private loginProgressSubscribers: (() => void)[] = [];
  private connectionAttempt = 0;
  private handshakeTimer?: number;
  private rejectedLoginPending = false;
  subscribeLoginProgress(callback: () => void) {
    this.loginProgressSubscribers.push(callback);
    return () => { this.loginProgressSubscribers = this.loginProgressSubscribers.filter(cb => cb !== callback); };
  }
  worldLoading = false;
  worldLoadError = "";
  private warpFrame = new WarpFrame();
  private warpPreloader = new WarpPreloader(this);

  setLoginPending(value: boolean, message = "Connecting to EOWorld…") {
    this.loginPending = value;
    this.loginStatus = value ? message : "";
    for (const callback of this.loginProgressSubscribers) callback();
  }

  holdRejectedLogin() {
    // Keep only a local pending screen, not an open socket or retry loop.
    this.rejectedLoginPending = true;
    this.connectionAttempt++;
    clearTimeout(this.handshakeTimer); this.handshakeTimer = undefined;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = undefined; }
    this.reconnecting = false;
    this.reconnectAttempts = 0;
    this.postConnectState = undefined;
    this.authenticationController.skipAutoLogin();
    this.bus.disconnect();
    this.setState(GameState.Login);
    this.setLoginPending(true);
  }
  container!: HTMLDivElement;
  app!: Application;
  worldContainer!: Container;
  uiContainer!: Container;
  minimapContainer!: Container;
  width = 800;
  height = 600;
  zoom = 1;
  tickCount = 0;
  bus = new PacketBus();
  packetLogStore = new PacketLogStore();
  config = getDefaultConfig();
  version: Version;
  challenge: number;
  hdid = getDeviceId();
  playerId = 0;
  characterId = 0;
  name = '';
  title = '';
  home = '';
  partner = '';
  guildName = '';
  guildTag = '';
  guildRank = 0;
  guildRankName = '';
  classId = 0;
  admin = AdminLevel.Player;
  level = 0;
  experience = 0;
  hp = 0;
  maxHp = 0;
  tp = 0;
  maxTp = 0;
  maxSp = 0;
  statPoints = 0;
  skillPoints = 0;
  karma = 0;
  baseStats = new CharacterBaseStats();
  secondaryStats = new CharacterSecondaryStats();
  equipment = new EquipmentPaperdoll();
  spells: Spell[] = [];
  weight = new Weight();
  mapId = 5;
  warpMapId = 0;
  warpQueued = false;
  state = GameState.Initial;
  postConnectState?: GameState;
  sessionId = 0;
  serverSettings: ServerSettings | null = null;
  motd = '';
  nearby: NearbyInfo;
  eif!: Eif;
  ecf!: Ecf;
  enf!: Enf;
  esf!: Esf;
  map!: Emf;
  mapRenderer: MapRenderer;
  downloadQueue: { type: FileType; id: number }[] = [];
  mousePosition: Vector2 | undefined;
  mouseCoords: Vector2 | undefined;
  viewportController: ViewportController;
  animationController: AnimationController;
  movementController: MovementController;
  keyboardController: KeyboardController;
  audioController: AudioController;
  authenticationController: AuthenticationController;
  bankController: BankController;
  bardController: BardController;
  barberController: BarberController;
  boardController: BoardController;
  chatController: ChatController;
  chestController: ChestController;
  cleanupController: CleanupController;
  drunkController: DrunkController;
  forgeController: ForgeController;
  jukeboxController: JukeboxController;
  commandController: CommandController;
  inventoryController: InventoryController;
  lockerController: LockerController;
  mapController: MapController;
  mouseController: MouseController;
  npcController: NpcController;
  questController: QuestController;
  sessionController: SessionController;
  shopController: ShopController;
  socialController: SocialController;
  statSkillController: StatSkillController;
  statsController: StatsController;
  itemProtectionController: ItemProtectionController;
  quakeController: QuakeController;
  spellController: SpellController;
  usageController: UsageController;
  tradeController: TradeController;
  guildController: GuildController;
  hotbarController: HotbarController;
  innController: InnController;
  alertController: AlertController;
  toastController: ToastController;
  partyController: PartyController;
  marriageController: MarriageController;
  pingController: PingController;
  npcMetadata = getNpcMetaData();
  weaponMetadata: Map<number, WeaponMetadata> = new Map();
  shieldMetadata = getShieldMetaData();
  effectMetadata = getEffectMetaData();
  hatMetadata = getHatMetadata();
  typing = false;
  reconnecting = false;
  edfs: {
    game1: Edf | null;
    game2: Edf | null;
    jukebox: Edf | null;
  } = {
    game1: null,
    game2: null,
    jukebox: null,
  };
  atlas: Atlas;
  gfxLoader = new GfxLoader();
  hotbarSlots: ISlot[] = [];
  sans11: Sans11Font;
  menuPlayerId = 0;
  minimapEnabled = false;
  minimapRenderer: MinimapRenderer;
  onlinePlayers: OnlinePlayer[] = [];
  playerTriggeredDisconnect = false;
  reconnectAttempts = 0;
  maxReconnectAttempts = 3;
  reconnectTimer?: number;
  locale = locales[defaultLocale];
  configController = new ConfigController();
  // Persistent Sets reused each tick to avoid per-tick allocation
  private readonly _activeCharIds = new Set<number>();
  private readonly _activeNpcIds = new Set<number>();

  constructor() {
    registerAllHandlers(this);
    this.bus.onPacketSent = (family, action, rawBytes, payload) => {
      this.packetLogStore.addEntry({
        source: 'client',
        family,
        action,
        timestamp: new Date(),
        rawBytes,
        payload,
      });
    };
    this.bus.onPacketReceived = (family, action, rawBytes, payload) => {
      this.packetLogStore.addEntry({
        source: 'server',
        family,
        action,
        timestamp: new Date(),
        rawBytes,
        payload,
      });
    };
    this.container = document.querySelector('#game-container')!;
    this.version = new Version();
    this.version.major = 0;
    this.version.minor = 0;
    this.version.patch = 28;
    this.challenge = 0;
    getEif().then(async (eif) => {
      this.eif = eif!;
      if (eif) {
        this.weaponMetadata = getWeaponMetaData();
      }
    });
    getEcf().then((ecf) => {
      this.ecf = ecf!;
    });
    getEnf().then((enf) => {
      this.enf = enf!;
    });
    getEsf().then((esf) => {
      this.esf = esf!;
    });
    getEdf(3).then((edf) => {
      this.socialController.naughtyWords =
        edf
          ?.getLine(0)
          ?.split(':')
          .filter((word) => word.length > 0) || [];
    });
    getEdf(4).then((edf) => {
      this.edfs.jukebox = edf;
      this.jukeboxController.loadTracks();
    });
    getEdf(5).then((edf) => {
      this.edfs.game1 = edf;
    });
    getEdf(6).then((edf) => {
      this.edfs.game2 = edf;
    });
    this.nearby = new NearbyInfo();
    this.nearby.characters = [];
    this.nearby.npcs = [];
    this.nearby.items = [];
    this.mapRenderer = new MapRenderer(this);
    this.minimapRenderer = new MinimapRenderer(this);
    this.viewportController = new ViewportController(this);
    this.animationController = new AnimationController(this);
    this.movementController = new MovementController(this);
    this.keyboardController = new KeyboardController(this);
    this.audioController = new AudioController(this);
    setAudioController(this.audioController);
    this.authenticationController = new AuthenticationController(this);
    this.bankController = new BankController(this);
    this.bardController = new BardController(this);
    this.barberController = new BarberController(this);
    this.boardController = new BoardController(this);
    this.chatController = new ChatController(this);
    this.chestController = new ChestController(this);
    this.cleanupController = new CleanupController(this);
    this.commandController = new CommandController(this);
    this.drunkController = new DrunkController(this);
    this.forgeController = new ForgeController(this);
    this.inventoryController = new InventoryController(this);
    this.itemProtectionController = new ItemProtectionController();
    this.alertController = new AlertController();
    this.lockerController = new LockerController(this);
    this.mapController = new MapController(this);
    this.mouseController = new MouseController(this);
    this.npcController = new NpcController(this);
    this.questController = new QuestController(this);
    this.quakeController = new QuakeController();
    this.sessionController = new SessionController(this);
    this.shopController = new ShopController(this);
    this.socialController = new SocialController(this);
    this.spellController = new SpellController(this);
    this.statSkillController = new StatSkillController(this);
    this.statsController = new StatsController();
    this.usageController = new UsageController(this);
    this.tradeController = new TradeController(this);
    this.guildController = new GuildController(this);
    this.hotbarController = new HotbarController(this);
    this.innController = new InnController(this);
    this.marriageController = new MarriageController(this);
    this.partyController = new PartyController(this);
    this.toastController = new ToastController();
    this.jukeboxController = new JukeboxController(this);
    this.pingController = new PingController(this);
    loadConfig().then((config) => {
      this.config = config;
      const txtHost =
        document.querySelector<HTMLInputElement>('input[name="host"]')!;

      if (txtHost) {
        if (this.config.staticHost) {
          txtHost!.classList.add('hidden');
        }
        txtHost!.value = config.host;
      }

      document.title = config.title;
    });
    this.atlas = new Atlas(this);
    this.sans11 = new Sans11Font(this.atlas);
  }

  async initPixi() {
    this.app = new Application();
    await this.app.init({
      width: this.viewportController.getGameWidth(),
      height: this.viewportController.getGameHeight(),
      background: '#000000',
      antialias: false,
    });

    this.app.renderer.canvas.id = 'game';
    this.app.renderer.canvas.style.imageRendering = 'pixelated';

    const gameEl = document.querySelector('#game');
    if (gameEl) {
      gameEl.replaceWith(this.app.renderer.canvas);
    } else {
      document
        .querySelector('#game-container')!
        .appendChild(this.app.renderer.canvas);
    }

    this.worldContainer = new Container();
    this.uiContainer = new Container();
    this.minimapContainer = new Container();

    this.app.stage.addChild(this.worldContainer);
    this.app.stage.addChild(this.uiContainer);
    this.app.stage.addChild(this.minimapContainer);

    // Sync CSS size now that the canvas is in the DOM
    this.viewportController.resizeCanvases();
  }

  getCharacterById(id: number): CharacterMapInfo | undefined {
    return this.nearby.characters.find((c) => c.playerId === id);
  }

  getPlayerCharacter(): CharacterMapInfo | undefined {
    return this.nearby.characters.find((c) => c.playerId === this.playerId);
  }

  getNpcByIndex(index: number): NpcMapInfo | undefined {
    return this.nearby.npcs.find((n) => n.index === index);
  }

  getItemByIndex(index: number): ItemMapInfo | undefined {
    return this.nearby.items.find((i) => i.uid === index);
  }

  getNpcMetadata(graphicId: number): NPCMetadata {
    const data = this.npcMetadata.get(graphicId);
    if (data) {
      return data;
    }

    return {
      xOffset: 0,
      yOffset: 0,
      xOffsetAttack: 0,
      yOffsetAttack: 0,
      animatedStanding: false,
      nameLabelOffset: 0,
      transparent: false,
    };
  }

  getEnfRecordById(id: number): EnfRecord | undefined {
    if (!this.enf) {
      return;
    }

    return this.enf.npcs[id - 1];
  }

  getEnfRecordByBehaviorId(
    type: NpcType,
    behaviorId: number,
  ): EnfRecord | undefined {
    if (!this.enf) {
      return;
    }

    return this.enf.npcs.find(
      (n) => n.type === type && n.behaviorId === behaviorId,
    );
  }

  getWeaponMetadata(graphicId: number): WeaponMetadata {
    // Check the live module-level map (populated by async load)
    // rather than only the snapshot, to avoid timing issues
    const live = getWeaponMetaData();
    const data = live.get(graphicId) || this.weaponMetadata.get(graphicId);
    if (data) {
      return data;
    }

    return { slash: 0, sfx: [SfxId.MeleeWeaponAttack], ranged: false };
  }

  getShieldMetadata(graphicId: number): ShieldMetadata {
    const data = this.shieldMetadata.get(graphicId);
    if (data) {
      return data;
    }

    return { back: false };
  }

  getHatMetadata(graphicId: number): HatMaskType {
    const data = this.hatMetadata.get(graphicId);
    if (data !== undefined) {
      return data;
    }

    return graphicId ? HatMaskType.HideHair : HatMaskType.Standard;
  }

  getEifRecordById(id: number): EifRecord | undefined {
    if (!this.eif) {
      return;
    }

    return this.eif.items[id - 1];
  }

  getEcfRecordById(id: number): EcfRecord | undefined {
    if (!this.ecf) {
      return;
    }

    return this.ecf.classes[id - 1];
  }

  getEsfRecordById(id: number): EsfRecord | undefined {
    if (!this.esf) {
      return;
    }

    return this.esf.skills[id - 1];
  }

  getResourceString(id: EOResourceID): string {
    const edf = this.edfs.game2;
    const line = edf?.getLine(id);
    if (!line) {
      return humanizeEnumName(EOResourceID[id]);
    }

    if (line.startsWith('*')) {
      return line.substring(1);
    }

    return line;
  }

  getDialogStrings(id: DialogResourceID): string[] {
    const edf = this.edfs.game1;
    const title = edf?.getLine(id);
    const body = edf?.getLine(id + 1);
    const fallback = humanizeEnumName(DialogResourceID[id]);

    return [title || 'Message', body || fallback || 'No message text available'];
  }

  getEffectMetadata(graphicId: number): EffectMetadata {
    const data = this.effectMetadata.get(graphicId);
    if (data) {
      return data;
    }

    return {
      hasBehindLayer: false,
      hasTransparentLayer: false,
      hasInFrontLayer: true,
      sfx: 0,
      frames: 4,
      loops: 2,
      offsetX: 0,
      offsetY: 0,
      animationType: EffectAnimationType.Static,
      verticalMetadata: null,
      positionOffsetMetadata: null,
      randomFlickeringMetadata: null,
    };
  }

  getPlayerCoords() {
    const playerCharacter = this.getPlayerCharacter();
    if (playerCharacter) {
      return { x: playerCharacter.coords.x, y: playerCharacter.coords.y };
    }

    return { x: 0, y: 0 };
  }

  setMousePosition(position: Vector2) {
    this.mousePosition = position;

    const player = this.getPlayerCoords();
    const playerScreen = isoToScreen(player);

    const mouseWorldX =
      position.x - this.viewportController.getHalfGameWidth() + playerScreen.x;
    const mouseWorldY =
      position.y -
      this.viewportController.getHalfGameHeight() +
      playerScreen.y +
      HALF_TILE_HEIGHT;

    this.mouseCoords = screenToIso({ x: mouseWorldX, y: mouseWorldY });

    if (this.mouseCoords.x < 0 || this.mouseCoords.y < 0) {
      this.mouseCoords = undefined;
    }
  }

  tick() {
    this.tickCount += 1;
    this.keyboardController.tick();
    this.mapRenderer.tick();

    if (this.state === GameState.InGame) {
      // Rebuild persistent Sets to avoid per-tick allocation
      this._activeCharIds.clear();
      for (const c of this.nearby.characters)
        this._activeCharIds.add(c.playerId);
      this._activeNpcIds.clear();
      for (const n of this.nearby.npcs) this._activeNpcIds.add(n.index);
      const activeCharIds = this._activeCharIds;
      const activeNpcIds = this._activeNpcIds;

      this.usageController.tick();
      this.itemProtectionController.tick();
      this.drunkController.tick();
      this.cleanupController.tick();
      this.spellController.tick();
      this.npcController.tick();
      this.pingController.tick();

      const { playerWalking, playerDying } = this.animationController.tick(
        activeCharIds,
        activeNpcIds,
      );

      this.mapController.tick();

      // A held last-good-map image does not render new animation frames. In
      // particular, death must not wait for an unrendered animation to finish
      // before accepting the server's authoritative respawn transfer.
      if (this.warpQueued && (this.worldLoading || (!playerWalking && !playerDying))) {
        this.sessionController.acceptWarp();
      }

      this.movementController.tick();
      this.quakeController.tick();
      this.warpPreloader.tick();
    }
  }

  render(interpolation: number) {
    if (this.worldLoading && this.state === GameState.InGame) {
      this.warpFrame.resize(this.app);
      return;
    }
    this.mapRenderer.update(interpolation);
    this.minimapRenderer.update(interpolation);
  }

  preserveWarpFrame(): void {
    if (!this.app || !this.map || this.state !== GameState.InGame) return;
    try { this.warpFrame.preserve(this.app); }
    catch (error) { console.warn('Unable to preserve the current map frame', error); }
  }

  releaseWarpFrame(renderDestination = false): void {
    if (renderDestination) {
      // Populate the destination under the snapshot before removing it. The
      // first visible frame is complete, never an empty/half-repacked atlas.
      this.mapRenderer.update(1);
      this.minimapRenderer.update(1);
    }
    this.warpFrame.release();
  }

  setMap(map: Emf) {
    // Atlas refresh owns its in-flight graphics batch. Invalidate by map
    // identity on the next refresh, never reset its mutable state here.
    this.map = map;
    // Old movement/death/effect animations cannot follow actors into a new map.
    this.animationController.characterAnimations.clear();
    this.animationController.pendingCharacterAnimations.clear();
    this.animationController.npcAnimations.clear();
    this.animationController.pendingNpcAnimations.clear();
    this.animationController.effects = [];
    this.npcController.clearAllTargets();
    this.spellController.cancelTargeting(true);
    this.spellController.spellCooldownTicks = 0;
    this.movementController.autoWalkPath = [];
    this.animationController.characterChats.clear();
    this.animationController.npcChats.clear();
    this.animationController.npcHealthBars.clear();
    this.animationController.characterHealthBars.clear();
    this.itemProtectionController.itemProtectionTimers.clear();
    this.jukeboxController.reset();
    if (this.map) {
      clearRectangles();
      this.mapRenderer.buildCaches();
      this.mapController.loadDoors();

      if (this.map.type === MapType.Pk) {
        this.audioController.playById(SfxId.EnterPkMap);
      }

      try {
        this.audioController.stopAmbientSound();
        if (this.map.ambientSoundId) this.audioController.startAmbientSound();
      } catch (error) {
        // Sound setup must not interrupt map installation or character login.
        console.warn('Map ambient audio failed; continuing without it', error);
      }

      try { this.audioController.updateListenerPosition(this.getPlayerCoords()); }
      catch (error) { console.warn('Unable to update audio listener position', error); }
      void this.audioController.handleMapMusic(
        this.map.musicId,
        this.map.musicControl,
      ).catch(error => console.warn('Map music unavailable; continuing without it', error));

      if (!this.map.mapAvailable) {
        this.minimapEnabled = false;
      }
    }
  }

  async loadMap(id: number): Promise<void> {
    const map = await getEmf(id);
    if (map) {
      this.setMap(map);
    }
  }

  showError(message: string, title = '') {
    this.alertController.show(title, message);
  }

  subscribeStateChanged(cb: (state: GameState) => void): void {
    this.stateChangedSubscribers.push(cb);
  }

  unsubscribeStateChanged(cb: (state: GameState) => void): void {
    this.stateChangedSubscribers = this.stateChangedSubscribers.filter(
      (s) => s !== cb,
    );
  }

  setState(state: GameState) {
    this.state = state;
    if (state === GameState.Initial) {
      this.releaseWarpFrame();
      this.worldLoading = false; this.worldLoadError = '';
    }
    if (state === GameState.Connected || state === GameState.Initial || state === GameState.InGame) {
      clearTimeout(this.handshakeTimer); this.handshakeTimer = undefined;
    }
    if (state !== GameState.Connected) this.setLoginPending(false);
    this.forgeController.clearOwnedSync();
    if (state === GameState.InGame) this.forgeController.requestOwnedSync();

    if (state === GameState.InGame && this.reconnecting) {
      this.reconnecting = false;
      this.reconnectAttempts = 0;
      this.toastController.showSuccess('Auto-reconnected!');
    }
    this.minimapEnabled = false;
    this.animationController.characterAnimations.clear();
    this.animationController.pendingCharacterAnimations.clear();
    this.animationController.npcAnimations.clear();
    this.animationController.pendingNpcAnimations.clear();
    this.animationController.characterChats.clear();
    this.animationController.npcChats.clear();
    this.npcController.queuedNpcChats.clear();
    this.animationController.npcHealthBars.clear();
    this.animationController.characterHealthBars.clear();
    this.animationController.characterEmotes.clear();
    this.animationController.effects = [];
    this.movementController.autoWalkPath = [];
    this.npcController.clearAllTargets();
    this.spellController.spellTarget = null;
    this.downloadQueue = [];
    this.usageController.idleTicks = INITIAL_IDLE_TICKS;
    this.drunkController.drunk = false;
    this.drunkController.drunkEmoteTicks = 0;
    this.spellController.cancelTargeting(true);
    this.spellController.spellCooldownTicks = 0;
    this.menuPlayerId = 0;
    this.onlinePlayers = [];
    this.inventoryController.equipmentSwap = null;
    this.itemProtectionController.itemProtectionTimers.clear();
    if (state === GameState.InGame) {
      this.questController.loadTrackedQuest();
      this.partyController.reset();
    }
    for (const cb of this.stateChangedSubscribers) cb(this.state);
    this.audioController.handleStateChange(state);
  }

  connect(nextState: GameState) {
    if (this.loginPending || this.rejectedLoginPending) return;
    this.playerTriggeredDisconnect = false;
    const attempt = ++this.connectionAttempt;
    this.setLoginPending(true);
    this.postConnectState = nextState;
    this.bus
      .connect(this.config.host, () => this.handleConnectionClose())
      .then(() => {
        if (attempt !== this.connectionAttempt) return;
        this.beginHandshake();
      })
      .catch((err) => {
        if (attempt !== this.connectionAttempt) return;
        this.setLoginPending(false);
        console.warn('Failed to connect to server', err);
        const strings = this.getDialogStrings(
          DialogResourceID.CONNECTION_SERVER_NOT_FOUND,
        );
        this.alertController.show(strings[0], strings[1]);
        this.postConnectState = undefined;
      });
  }

  private handleConnectionClose() {
    if (this.rejectedLoginPending) return;
    clearTimeout(this.handshakeTimer); this.handshakeTimer = undefined;
    // A closed socket cannot complete the current WarpAccept/WarpAgree pair.
    // Clear it before reconnecting so stale map/atlas work cannot affect the
    // next login attempt.
    this.sessionController.abortPendingWarp();
    this.setLoginPending(false);
    if (this.playerTriggeredDisconnect) {
      this.playerTriggeredDisconnect = false;
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    const session = this.getSavedSessionForCurrentCharacter();
    if (!session) {
      this.reconnecting = false;
      const strings = this.getDialogStrings(
        DialogResourceID.CONNECTION_LOST_CONNECTION,
      );
      this.alertController.show(strings[0], strings[1]);
      this.setState(GameState.Initial);
      return;
    }

    this.attemptReconnect(session);
  }

  private attemptReconnect(session: SavedSession) {
    if (this.rejectedLoginPending) return;
    this.reconnectAttempts++;

    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      this.reconnecting = false;
      this.toastController.showError('Auto-reconnect failed after 3 attempts');
      this.setState(GameState.Initial);
      this.authenticationController.clearSession();
      this.reconnectAttempts = 0;
      return;
    }

    this.reconnecting = true;
    this.playerTriggeredDisconnect = false;
    const attempt = ++this.connectionAttempt;
    this.setLoginPending(true);
    this.authenticationController.selectSession(session.username);
    this.postConnectState = GameState.Login;
    this.bus
      .connect(this.config.host, () => this.handleConnectionClose())
      .then(() => {
        if (attempt !== this.connectionAttempt) return;
        this.beginHandshake();
      })
      .catch(() => {
        if (attempt !== this.connectionAttempt) return;
        this.setLoginPending(false);
        this.toastController.showWarning(
          `Auto-reconnect failed: Retrying.. Attempt #${this.reconnectAttempts}`,
        );
        this.reconnectTimer = window.setTimeout(() => {
          this.attemptReconnect(session);
        }, 2000);
      });
  }

  private getSavedSessionForCurrentCharacter(): SavedSession | null {
    if (this.characterId === 0) {
      return null;
    }

    const sessions = this.authenticationController.getSessions();
    return sessions.find((s) => s.lastCharacterId === this.characterId) ?? null;
  }

  private beginHandshake() {
    clearTimeout(this.handshakeTimer);
    this.handshakeTimer = window.setTimeout(() => {
      this.bus.disconnect(); this.handleConnectionClose();
    }, 15000);
    const packet = new InitInitClientPacket();
    packet.challenge = this.challenge = randomRange(1, MAX_CHALLENGE);
    packet.hdid = this.hdid;
    packet.version = this.version;
    this.bus.send(packet);
  }

  handleSessionReplaced() {
    this.disconnect();
    this.playerTriggeredDisconnect = true;
    this.characterId = 0;
    this.postConnectState = undefined;
    clearTimeout(this.handshakeTimer); this.handshakeTimer = undefined;
    this.authenticationController.skipAutoLogin();
    this.alertController.show('Signed out', 'This account was signed in from another browser. This session has been closed.');
  }

  disconnect() {
    this.connectionAttempt++;
    // An explicit disconnect (logout, failed initial map load, or user exit)
    // must not be interpreted as an unexpected socket loss and auto-reconnect
    // the same character into the same failure again.
    this.playerTriggeredDisconnect = true;
    this.reconnecting = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.reconnectAttempts = 0;
    this.setState(GameState.Initial);
    this.authenticationController.clearSession();
    this.authenticationController.skipAutoLogin();
    if (this.bus) {
      this.bus.disconnect();
    }
  }

  refresh() {
    this.bus.send(new RefreshRequestClientPacket());
  }

  setNpcDeathAnimation(index: number) {
    const npc = this.nearby.npcs.find((n) => n.index === index);
    if (!npc) {
      return;
    }

    const current = this.animationController.npcAnimations.get(index);
    this.animationController.pendingNpcAnimations.set(
      index,
      new NpcDeathAnimation(current),
    );
  }

  setCharacterDeathAnimation(playerId: number) {
    const character = this.getCharacterById(playerId);
    if (!character) {
      return;
    }

    const current = this.animationController.characterAnimations.get(playerId);
    this.animationController.pendingCharacterAnimations.set(
      playerId,
      new CharacterDeathAnimation(current),
    );
  }

  toggleMinimap() {
    if (!this.map.mapAvailable) {
      this.toastController.showWarning(
        this.getResourceString(EOResourceID.STATUS_LABEL_NO_MAP_OF_AREA),
      );
      return;
    }

    this.minimapEnabled = !this.minimapEnabled;
  }

  addItemDrop(item: ItemMapInfo, protectedFor = 0, ownerId = 0) {
    this.nearby.items.push(item);
    if (protectedFor) {
      this.itemProtectionController.itemProtectionTimers.set(item.uid, {
        ticks: protectedFor,
        ownerId,
      });
    }
  }

  getNpcKilledMessage(
    data: NpcKilledData,
    exp = 0,
    levelUp = false,
  ): string | null {
    let killerName: string | undefined;
    if (data.killerId === this.playerId) {
      killerName = this.locale.shared.wordYou;
    } else {
      killerName = this.partyController.members.find(
        (m) => m.playerId === data.killerId,
      )?.name;
    }

    if (!killerName) {
      return null;
    }

    killerName = capitalize(killerName);

    const npc = this.nearby.npcs.find((n) => n.index === data.npcIndex);
    const record = npc ? this.getEnfRecordById(npc.id) : undefined;
    const npcName = record?.name;

    if (!npcName) {
      return null;
    }

    const messages = [
      formatLocaleString(this.locale.messages.killedNpc, {
        killer: killerName,
        npc: npcName,
      }),
    ];

    if (data.killerId === this.playerId && exp > 0) {
      messages.push(
        formatLocaleString(this.locale.messages.gainedExp, {
          exp: exp.toLocaleString(),
        }),
      );
    }

    if (levelUp) {
      messages.push(
        formatLocaleString(this.locale.messages.levelUp, {
          level: this.level.toLocaleString(),
        }),
      );
    }

    if (data.dropId && data.dropAmount) {
      const itemRecord = this.getEifRecordById(data.dropId);
      const itemName = itemRecord?.name ?? this.locale.shared.wordUnknown;
      if (itemName) {
        const amountText =
          data.dropAmount > 1 ? `${data.dropAmount.toLocaleString()} ` : '';
        messages.push(
          formatLocaleString(this.locale.messages.npcDrop, {
            item: `${amountText}${itemName}`,
          }),
        );
      }
    }

    return messages.join(' ');
  }

  getExpShareMessage(exp: number, levelUp = false): string {
    const messages = [
      formatLocaleString(this.locale.messages.expShare, {
        exp: exp.toLocaleString(),
      }),
    ];

    if (levelUp) {
      messages.push(
        formatLocaleString(this.locale.messages.levelUp, {
          level: this.level.toLocaleString(),
        }),
      );
    }

    return messages.join(' ');
  }
}
